<template>
  <div class="card-item card-item-status">
    <icon-status class="icon" v-ifield-b-tooltip="iconTooltipText" />
    <span
      ref="truncTarget"
      class="text ellipsis"
      v-ifield-b-tooltip="tooltipOption"
    >
      {{ text }}
    </span>
    <div
      class="color-dot"
      v-bind:class="{ bordered: isBorderRequired }"
      v-bind:style="{ '--color': color, '--border-color': borderColor }"
    />
  </div>
</template>

<script>
import { toRefs } from 'vue'
import IconStatus from '@/assets/ifield/ic_status.svg?component'
import { useIsTruncated } from '@/composables/UseIsTruncated'
import { useUserStatusAdditionalItemStatus } from '@/composables/user/additionalItem'
import ColorUtil from '@/js/util/ColorUtil.js'
import { hsl } from 'd3-color'

// borderをつけるコントラスト比の閾値
// 閾値は7だとborderが付きすぎるので調整してある
const CONTRAST_THRESHOLD = 1.5

export default {
  name: 'AppCardMemberItemStatus',
  components: {
    IconStatus,
  },
  inheritAttrs: false,
  props: {
    /**
     * ステイタス名
     */
    statusName: {
      type: String,
      default: null,
    },
    /**
     * ステイタスカラー
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * 背景色
     */
    backgroundColor: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const { statusName } = toRefs(props)
    const { truncTarget, isTruncated } = useIsTruncated()
    const { text } = useUserStatusAdditionalItemStatus({ statusName })
    return { truncTarget, isTruncated, text }
  },
  computed: {
    /**
     * 背景色の相対輝度
     * @type {number}
     */
    backgroundColorRelativeLuminance: function () {
      if (!this.backgroundColor) return null
      return ColorUtil.calcRelativeLuminance(this.backgroundColor)
    },
    /**
     * ステイタス色の相対輝度
     * @type {number}
     */
    colorRelativeLuminance: function () {
      if (!this.color) return null
      return ColorUtil.calcRelativeLuminance(this.color)
    },
    /**
     * 背景色とステイタス色のコントラスト比
     * @type {Number}
     */
    contrastRatio: function () {
      if (!this.color || !this.backgroundColor) return null
      return this.$_calcContrastRatio(this.color)
    },
    /**
     * borderを描画する場合はtrue
     * @type {boolean}
     */
    isBorderRequired: function () {
      if (!this.color || !this.backgroundColor) return false
      return this.contrastRatio < CONTRAST_THRESHOLD
    },
    /**
     * border色
     * @type {String}
     */
    borderColor: function () {
      if (!this.isBorderRequired) return undefined
      // コントラスト比を満たすまで調整
      let ratio = this.contrastRatio
      let color = this.color
      while (ratio < CONTRAST_THRESHOLD) {
        color = hsl(color).darker().formatHex()
        ratio = this.$_calcContrastRatio(color)
      }
      return color
    },
    /**
     * アイコン部ツールチップテキスト
     * @type {string}
     */
    iconTooltipText: function () {
      return `${this.$t('appCardMemberItemStatus.status')} : ${this.text}`
    },
    /**
     * ツールチップオプション
     * @type {Object}
     */
    tooltipOption: function () {
      return {
        title: this.text,
        disabled: !this.isTruncated,
      }
    },
  },
  methods: {
    /**
     * 相対輝度によるコントラスト比を求める
     * @see https://waic.jp/docs/WCAG-TECHS/G17.html
     * @param {string} color 表面カラー
     */
    $_calcContrastRatio: function (color) {
      if (!color || !this.backgroundColor) return null

      const colorRelativeLuminance = ColorUtil.calcRelativeLuminance(color)
      const isColorBrighter =
        colorRelativeLuminance > this.backgroundColorRelativeLuminance
      const l1 = isColorBrighter
        ? colorRelativeLuminance
        : this.backgroundColorRelativeLuminance
      const l2 = isColorBrighter
        ? this.backgroundColorRelativeLuminance
        : colorRelativeLuminance
      return (l1 + 0.05) / (l2 + 0.05)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/member/card-member-item';

.color-dot {
  --color: #f6f6f6;
  --border-color: #b9bdbf;

  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  background-color: var(--color);
  border-radius: 50%;

  &.bordered {
    border: 1px solid var(--border-color);
  }
}
</style>
